import {
	casesLawyerService,
	GET_LAWYER_CASE_PHASES,
	GET_LAWYER_CASE_STATUSES,
} from '@/api';
import { Icon, LabelLawyerState, Typography } from '@/components/common';
import { Options } from '@/components/ui/combobox';
import { OptionsMultiple } from '@/components/ui/comboboxMultiple';
import Icons from '@/const/icons';
import useDeviceWidth from '@/hooks/useDeviceWidth';
import useModal from '@/hooks/useModal';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import React, { Dispatch, SetStateAction } from 'react';

interface ModalFilterCasesProps {
	isOpen: boolean;
	setIsModalOpen: Dispatch<SetStateAction<boolean>>;
}
const ModalFilterCases: React.FC<ModalFilterCasesProps> = ({
	isOpen,
	setIsModalOpen,
}) => {
	const searchParams = useSearchParams();
	const params = new URLSearchParams(searchParams);
	const width = useDeviceWidth();
	const pathname = usePathname();
	const { replace } = useRouter();
	const { Modal, show, hide } = useModal();
	const [filters, setFilters] = React.useState<OptionsMultiple[]>([]);

	const { data: caseStatuses } = useQuery({
		queryKey: [GET_LAWYER_CASE_STATUSES],
		queryFn: () => casesLawyerService.getCaseStatuses(),
	});

	const { data: casePhases } = useQuery({
		queryKey: [GET_LAWYER_CASE_PHASES],
		queryFn: () => casesLawyerService.getCasePhases(),
	});
	const getIcon = (name: string) => {
		let src = '';
		if (name === 'Investigation') {
			src = Icons.search;
		} else if (name === 'Applicant Intake') {
			src = Icons.tag;
		} else if (name === 'Active Case') {
			src = Icons.folderOpen;
		} else if (name === 'Settlement') {
			src = Icons.bank;
		}

		return <Icon src={src} width={18} height={18} className="text-primary" />;
	};

	React.useEffect(() => {
		if (isOpen) {
			show();
		}
	}, [isOpen]);

	React.useEffect(() => {
		if (caseStatuses?.data && casePhases?.data) {
			const casesStatusList =
				caseStatuses?.data.map(({ id, name }) => ({
					value: String(id),
					label: name,
					icon: <LabelLawyerState status={name} withLabel={false} />,
				})) || [];

			const casesPhasesList =
				casePhases?.data.map(({ id, name }) => ({
					value: String(id),
					label: name,
					icon: getIcon(name),
				})) || [];

			const initOptions: Options[] = [
				{
					label: 'View All',
					value: 'View All',
					icon: (
						<Icon
							src={Icons.list}
							width={18}
							height={18}
							className="text-primary"
						/>
					),
				},

				{
					label: 'Alphabetical Order',
					value: 'alphabetical',
					icon: (
						<Icon
							src={Icons.alphabeticalOrder}
							width={18}
							height={18}
							className="text-primary"
						/>
					),
				},
			];

			const groupFilters: OptionsMultiple[] = [
				{
					title: '',
					options: initOptions,
				},
				{
					title: 'Case Phase',
					options: casesPhasesList,
				},
				{
					title: 'Case Status',
					options: casesStatusList,
				},
			];
			setFilters(groupFilters);
		}
	}, [caseStatuses, casePhases]);

	const clearFilters = () => {
		params.delete('status');
		params.delete('phase');
		params.delete('alphabetical');

		replaceUrl();
	};

	const handleAlphabeticalOrder = () => {
		const isAlphabetical = params.get('alphabetical');
		if (isAlphabetical) {
			params.delete('alphabetical');
		} else {
			params.set('alphabetical', 'true');
		}
		replaceUrl();
	};

	const handleChange = (group: string, id: string | undefined) => {
		if (id?.toLocaleLowerCase() === 'view all') {
			clearFilters();
			hide();
			setIsModalOpen(false);
			return;
		}

		if (id === 'alphabetical') {
			handleAlphabeticalOrder();
			hide();
			setIsModalOpen(false);
			return;
		}

		if (group === 'Case Status') {
			if (id) {
				params.delete('phase');
				params.set('status', id);
			} else {
				params.delete('status');
			}
		}

		if (group === 'Case Phase') {
			if (id) {
				params.delete('status');
				params.set('phase', id);
			} else {
				params.delete('phase');
			}
		}
		hide();
		setIsModalOpen(false);
		replaceUrl();
	};

	const replaceUrl = () => {
		params.set('page', '1');
		replace(`${pathname}?${params.toString()}`);
	};

	const handleCloseModal = (): void => {
		hide();
		setIsModalOpen(false);
	};
	return (
		<Modal
			avoidClose
			classNameCardContainer={'!px-4 !py-6'}
			classNameContainerChildren={'!max-h-[90vh]'}
		>
			<div className="w-full flex flex-col " style={{ width: width - 80 }}>
				<div className="flex items-center justify-center mb-[18px]">
					<Typography type="semibold-16" className="w-full">
						Filter by
					</Typography>
					<button className="" onClick={handleCloseModal}>
						<X />
					</button>
				</div>
				{/* map here */}
				{filters.map((filterGroup, index) => (
					<div key={index} className="flex flex-col px-4">
						{filterGroup.title && (
							<div className="py-4 w-full">
								<Typography type="semibold-16" className="text-start">
									{filterGroup.title}
								</Typography>
							</div>
						)}
						{/* Map through options in each group */}
						<div className="flex flex-col">
							{filterGroup.options.map((option, i) => (
								<div
									key={i}
									className="flex items-center cursor-pointer py-3"
									onClick={() => handleChange(filterGroup.title, option.value)}
								>
									{option.icon}
									<Typography type="regular-16" className="ml-2">
										{option.label}
									</Typography>
								</div>
							))}
						</div>
					</div>
				))}
			</div>
		</Modal>
	);
};

export default ModalFilterCases;
